import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { CurriculumQuiz, CurriculumQuizQuestion } from '@drivingdoc/api-client/generated/types';

const newOption = (label: string, isCorrect = false) => ({
  id: crypto.randomUUID(),
  label,
  isCorrect,
});

const newQuestion = (): CurriculumQuizQuestion => ({
  id: crypto.randomUUID(),
  type: 'multiple_choice',
  prompt: 'New question',
  options: [newOption('Option 1', true), newOption('Option 2')],
});

export default function QuizEditor({
  quiz,
  onChange,
  onDelete,
}: {
  quiz: CurriculumQuiz;
  onChange: (quiz: CurriculumQuiz) => void;
  onDelete: () => void;
}) {
  const updateQuestion = (index: number, question: CurriculumQuizQuestion) => {
    const questions = [...quiz.questions];
    questions[index] = question;
    onChange({ ...quiz, questions });
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row">
            <TextField
              label="Quiz title"
              value={quiz.title}
              onChange={(event) => onChange({ ...quiz, title: event.target.value })}
              fullWidth
            />
            <IconButton aria-label="Delete quiz" onClick={onDelete}>
              <DeleteIcon />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Passing score"
              type="number"
              value={quiz.passingScorePercent}
              onChange={(event) => onChange({ ...quiz, passingScorePercent: Number(event.target.value) })}
              inputProps={{ min: 0, max: 100 }}
            />
            <TextField
              label="Minimum seconds"
              type="number"
              value={quiz.minTimeSeconds}
              onChange={(event) => onChange({ ...quiz, minTimeSeconds: Number(event.target.value) })}
              inputProps={{ min: 0 }}
              helperText="Default: 60 × question count. 0 = no seat-time gate."
            />
            <FormControlLabel
              label="Required"
              control={
                <Checkbox
                  checked={quiz.required}
                  onChange={(event) => onChange({ ...quiz, required: event.target.checked })}
                />
              }
            />
          </Stack>
          {quiz.questions.map((question, questionIndex) => (
            <Card key={question.id} variant="outlined">
              <CardContent>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="Question"
                      value={question.prompt}
                      onChange={(event) => updateQuestion(questionIndex, { ...question, prompt: event.target.value })}
                      fullWidth
                    />
                    <TextField
                      select
                      label="Type"
                      value={question.type}
                      onChange={(event) => {
                        const type = event.target.value as CurriculumQuizQuestion['type'];
                        updateQuestion(questionIndex, {
                          ...question,
                          type,
                          options:
                            type === 'true_false'
                              ? [newOption('True', true), newOption('False')]
                              : [newOption('Option 1', true), newOption('Option 2')],
                        });
                      }}
                      sx={{ minWidth: 180 }}
                    >
                      <MenuItem value="multiple_choice">Multiple choice</MenuItem>
                      <MenuItem value="true_false">True / false</MenuItem>
                    </TextField>
                    {quiz.questions.length > 1 && (
                      <IconButton
                        aria-label="Delete question"
                        onClick={() =>
                          onChange({
                            ...quiz,
                            questions: quiz.questions.filter((_, index) => index !== questionIndex),
                          })
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Stack>
                  <Typography variant="caption">Select the correct answer.</Typography>
                  <RadioGroup
                    value={question.options.find((option) => option.isCorrect)?.id ?? ''}
                    onChange={(event) =>
                      updateQuestion(questionIndex, {
                        ...question,
                        options: question.options.map((option) => ({
                          ...option,
                          isCorrect: option.id === event.target.value,
                        })),
                      })
                    }
                  >
                    {question.options.map((option, optionIndex) => (
                      <Stack key={option.id} direction="row" alignItems="center">
                        <Radio value={option.id} />
                        <TextField
                          value={option.label}
                          onChange={(event) => {
                            const options = [...question.options];
                            options[optionIndex] = { ...option, label: event.target.value };
                            updateQuestion(questionIndex, { ...question, options });
                          }}
                          disabled={question.type === 'true_false'}
                          fullWidth
                        />
                        {question.type === 'multiple_choice' && question.options.length > 2 && (
                          <IconButton
                            aria-label="Delete answer"
                            onClick={() => {
                              const options = question.options.filter((_, index) => index !== optionIndex);
                              if (option.isCorrect) options[0] = { ...options[0], isCorrect: true };
                              updateQuestion(questionIndex, { ...question, options });
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Stack>
                    ))}
                  </RadioGroup>
                  {question.type === 'multiple_choice' && (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() =>
                        updateQuestion(questionIndex, {
                          ...question,
                          options: [...question.options, newOption(`Option ${question.options.length + 1}`)],
                        })
                      }
                    >
                      Add answer
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => onChange({ ...quiz, questions: [...quiz.questions, newQuestion()] })}
          >
            Add question
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
